import React from "react";
import type { ApolloClient } from "@apollo/client";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { get, isEmpty } from "lodash";
import { push } from "store/actions/router";

import SelfEmployedFlow from "./SelfEmployedFlow";
import Alert from "components/Alert";
import IconSpinner from "components/IconSpinner";
import ProgressBar from "components/ProgressBar";
import ContributionSelector from "components/ContributionSelector";
import { createErrorSelector, createLoadingSelector } from "store/selectors";
import {
  getEmployerLinkRequests,
  getInvestmentProfile,
  getUserState,
} from "actions/userActions";
import { getAllAccounts } from "actions/accountActions";

import {
  accountConstants,
  contributionConstants,
  userConstants,
} from "actions/types";
import { scrollToTop } from "utils/dom";
import { getIraAccountIdSelector } from "store/selectors/user";
import {
  getContributionRecommendations,
  makeOnboardingPayrollContribution,
} from "actions/contributionActions";

import "./SetupContribution.scss";
import { IndividualPayrollContributionPending } from "statics/states";
import { INVESTMENT_PROFILE } from "statics/onboardingSteps";
import type { RootState } from "types/state";
import type { AddOnboardingPayCheckContributionMutationVariables } from "types/graphql";

const actions = [
  userConstants.USER_EMPLOYER_LINK_REQUESTS,
  accountConstants.GET_ACCOUNTS,
  userConstants.GET_INVESTMENT_PROFILE,
  contributionConstants.ONBOARDING_PAYCHECK_CONTRIBUTION,
  contributionConstants.GET_CONTRIBUTION_RECOMMENDATIONS,
  userConstants.USER_STATE,
];

const isSubmitting = createLoadingSelector([
  contributionConstants.ONBOARDING_PAYCHECK_CONTRIBUTION,
  // PRESERVED BUG (conversion-only): USER_STATE lives on userConstants, not
  // contributionConstants — this resolves to `undefined` at runtime, so the
  // USER_STATE action never contributes to this loading selector.
  // @ts-expect-error preserved pre-existing bug — see comment above
  contributionConstants.USER_STATE,
]);

const errorSelector = createErrorSelector(actions);

// `recommendations` is typed as `object` in ContributionState; the reducer
// stores the GetRecommendedContributionAmounts query result, so read the keys
// the component consumes through a local view of that shape.
interface ContributionRecommendations {
  scheduleFrequency?: string;
  daily?: {
    recommended: number;
    other: number[];
    isMaxContributionOption?: boolean;
  };
  perPayPeriod?: {
    recommended: number;
    other: number[];
    isMaxContributionOption?: boolean;
  };
}

const mapStateToProps = (state: RootState) => {
  const { dailyMax, perPayPeriodMax, recommendations } = state.contribution;
  const { scheduleFrequency } = recommendations as ContributionRecommendations;
  return {
    error: errorSelector(state),
    groupId: get(state.user.employerLinkRequests, "0.group.id", null),
    dailyMax,
    perPayPeriodMax,
    dailyRecommendations: (recommendations as ContributionRecommendations)
      .daily,
    perPayPeriodRecommendations: (
      recommendations as ContributionRecommendations
    ).perPayPeriod,
    scheduleFrequency,
    hasLinkRequest: !isEmpty(state.user.employerLinkRequests),
    accountId: getIraAccountIdSelector(state),
    isSubmitting: isSubmitting(state),
    userState: get(state, "user.userState.state", ""),
  };
};
const mapDispatchToProps = {
  push,
  getEmployerLinkRequests,
  getInvestmentProfile,
  getContributionRecommendations,
  getUserState,
  makeOnboardingPayrollContribution,
  getAllAccounts,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<PropsFromRedux>;

interface State {
  initialFetching: boolean;
}

class SetupContribution extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      initialFetching: true,
    };
  }

  async componentDidMount() {
    window.analytics.page("Setup Contribution");
    scrollToTop();
    await this.props.getAllAccounts(this.props.client as ApolloClient<object>);
    await this.props.getEmployerLinkRequests(
      this.props.client as ApolloClient<object>
    );
    await this.props.getInvestmentProfile(
      this.props.client as ApolloClient<object>
    );
    await this.props.getContributionRecommendations(
      this.props.client as ApolloClient<object>,
      {
        groupId: this.props.groupId,
        isSelfEmployed: !this.props.hasLinkRequest,
      }
    );

    this.setState({ initialFetching: false });
  }

  componentDidUpdate(prevProps: Props) {
    // 1099 signup can short circuit into an employer signup, need to catch that here
    const selfEmployedLinkedEmployer =
      this.props.hasLinkRequest && !prevProps.hasLinkRequest;
    if (selfEmployedLinkedEmployer) {
      this.setState({ initialFetching: true });
      void this.props
        .getContributionRecommendations(
          this.props.client as ApolloClient<object>,
          {
            groupId: this.props.groupId,
          }
        )
        .then(() => {
          this.setState({ initialFetching: false });
        });
    }
  }

  _submitPayCheckContribution = async (
    amount: number,
    isDailyCustom: boolean
  ) => {
    try {
      await this.props.makeOnboardingPayrollContribution(
        this.props.client as ApolloClient<object>,
        {
          isDailyCustom,
          groupId: this.props.groupId,
          amount: +amount,
          accountId: this.props.accountId,
        } as unknown as AddOnboardingPayCheckContributionMutationVariables
      );
      await this.props.getUserState(this.props.client as ApolloClient<object>);
      window.analytics.track("Submitted Contribution");
      this.props.push("/dashboard");
    } catch (error) {
      return error;
    }
  };

  render() {
    const noErrorNotLoading = !this.props.error && !this.state.initialFetching;
    const shouldShowProgressBar =
      this.props.hasLinkRequest &&
      this.props.userState === IndividualPayrollContributionPending;
    return (
      <>
        {shouldShowProgressBar && (
          <ProgressBar
            isEmployer={false}
            activeStepId={INVESTMENT_PROFILE.id}
            progressPercent={"90"}
          />
        )}
        <div id="setup-contributions">
          <div className="mega-container">
            <div className="main-content">
              {this.state.initialFetching && <IconSpinner centered />}
              {this.props.error && (
                <Alert type="error" msg={this.props.error} />
              )}
              {noErrorNotLoading && !this.props.hasLinkRequest && (
                <SelfEmployedFlow />
              )}
              {noErrorNotLoading &&
                this.props.hasLinkRequest &&
                !isEmpty(this.props.perPayPeriodRecommendations) && (
                  <ContributionSelector
                    // @ts-expect-error redux ContributionState types this as
                    // number | null; the selector passes the raw value (incl.
                    // null) through, which the component treats as optional.
                    dailyMax={this.props.dailyMax}
                    // @ts-expect-error redux ContributionState types this as
                    // number | null; the selector passes the raw value (incl.
                    // null) through, which the component treats as optional.
                    perPayPeriodMax={this.props.perPayPeriodMax}
                    dailyRecommendations={this.props.dailyRecommendations}
                    perPayPeriodRecommendations={
                      this.props.perPayPeriodRecommendations
                    }
                    error={this.props.error}
                    onSubmit={this._submitPayCheckContribution}
                    isSubmitting={this.props.isSubmitting}
                    // @ts-expect-error scheduleFrequency may be undefined here;
                    // the original passed it through unguarded as the schedule
                    // frequency, so preserve that exact value.
                    schedule={{ frequency: this.props.scheduleFrequency }}
                    isPaycheck={true}
                  >
                    <section className="page-title-wrap">
                      <article className="text-cell">
                        <h1 className="page-title">
                          With Icon, you have the freedom to choose how much of
                          your monthly pay you set aside to your retirement
                          account.
                        </h1>
                        <p className="page-subtext">
                          Please select the amount that{"'"}s good for you or
                          enter in a custom amount. You can modify your
                          contribution amount at any time.
                        </p>
                      </article>
                    </section>
                  </ContributionSelector>
                )}
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default connector(withApollo<Props>(SetupContribution));
